import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchMetadata, type AppMetadata, type DeliveryWindow } from '~/features/dashboard/api-config';
import { getWindowMinutes, type DeliveryWindowPreset } from '~/shared/utils/delivery-window';

type MetadataContextType = {
  windows: DeliveryWindow[];
  /** Autocall window presets: standard windows first, then the REF-0007 location windows */
  presets: DeliveryWindowPreset[];
  jobLocations: string[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
};

const DeliveryWindowsContext = createContext<MetadataContextType | undefined>(undefined);

export function DeliveryWindowsProvider({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();
  const [metadata, setMetadata] = useState<AppMetadata>({ deliveryWindows: [], jobLocations: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadMetadata = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchMetadata();
      setMetadata(data);
    } catch (err) {
      const errObj = err instanceof Error ? err : new Error('Failed to load metadata');
      setError(errObj);
      console.error('Error loading metadata:', errObj);
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch once after login. Skip if we already have data (avoids duplicate calls from re-renders/Strict Mode).
  useEffect(() => {
    if (accounts.length === 0) {
      setLoading(false);
      setMetadata({ deliveryWindows: [], jobLocations: [] });
      setError(null);
      return;
    }
    if (metadata.deliveryWindows.length > 0 || metadata.jobLocations.length > 0) return;
    loadMetadata();
  }, [accounts.length, loadMetadata, metadata.deliveryWindows.length, metadata.jobLocations.length]);

  const presets = useMemo<DeliveryWindowPreset[]>(() => {
    if (metadata.deliveryWindowPresets?.length) return metadata.deliveryWindowPresets;
    // Older backend without presets: offer the standard windows only
    return metadata.deliveryWindows.flatMap((w) => {
      const minutes = getWindowMinutes(w);
      return minutes ? [{ ...w, ...minutes, group: 'standard' as const }] : [];
    });
  }, [metadata]);

  const contextValue = useMemo(
    () => ({
      windows: metadata.deliveryWindows,
      presets,
      jobLocations: metadata.jobLocations,
      loading,
      error,
      refresh: loadMetadata,
    }),
    [metadata, presets, loading, error, loadMetadata]
  );

  return (
    <DeliveryWindowsContext.Provider value={contextValue}>
      {children}
    </DeliveryWindowsContext.Provider>
  );
}

export function useDeliveryWindows() {
  const context = useContext(DeliveryWindowsContext);
  if (context === undefined) {
    throw new Error('useDeliveryWindows must be used within a DeliveryWindowsProvider');
  }
  return context;
}
